'use client';

import { useRouter } from 'next/navigation';
import { type LoginState, type RegisterState, type ForgotPasswordState } from '@pixum/auth-ui';
import { validatePayPalExpressAddressesAction } from '../../actions/validatePayPalExpressAddressesAction';
import {
  trackLoginSuccess,
  trackLoginError,
  trackRegisterSuccess,
  trackRegisterError,
  trackForgotPasswordSuccess,
  trackForgotPasswordError
} from '../../analytics/auth';
import { REFRESH_SITE_STORAGE_KEY } from '../../constants/auth';

interface UseCartAuthHandlersOptions {
  loginAction: (prevState: LoginState, formData: FormData) => Promise<LoginState>;
  registerAction: (prevState: RegisterState, formData: FormData) => Promise<RegisterState>;
  forgotPasswordAction: (prevState: ForgotPasswordState, formData: FormData) => Promise<ForgotPasswordState>;
  getPostLoginRedirectAction: () => Promise<string>;
  isPayPalExpress: boolean;
  checkoutHref: string;
  addressHref: string;
  setLoginError: (error: string | null) => void;
  setIsForgotPasswordSuccess: (value: boolean) => void;
}

interface UseCartAuthHandlersResult {
  handleLogin: (prevState: LoginState, formData: FormData) => Promise<LoginState>;
  handleRegister: (prevState: RegisterState, formData: FormData) => Promise<RegisterState>;
  handleForgotPassword: (prevState: ForgotPasswordState, formData: FormData) => Promise<ForgotPasswordState>;
}

export function useCartAuthHandlers({
  loginAction,
  registerAction,
  forgotPasswordAction,
  getPostLoginRedirectAction,
  isPayPalExpress,
  checkoutHref,
  addressHref,
  setLoginError,
  setIsForgotPasswordSuccess
}: UseCartAuthHandlersOptions): UseCartAuthHandlersResult {
  const router = useRouter();

  async function handlePostLoginRedirect(): Promise<void> {
    if (isPayPalExpress) {
      const validation = await validatePayPalExpressAddressesAction();

      router.push(validation.success ? checkoutHref : addressHref);
    } else {
      const redirectUrl = await getPostLoginRedirectAction();

      router.push(redirectUrl);
    }
  }

  async function handleLogin(prevState: LoginState, formData: FormData): Promise<LoginState> {
    const result = await loginAction(prevState, formData);

    if (result === null) {
      trackLoginSuccess();

      // Do NOT call router.refresh() here: this is an UNAUTHENTICATED_ONLY route, so refresh
      // triggers a middleware redirect to overview that races with router.push(redirectUrl) and wins.
      // The destination RSC picks up the new JWT cookie automatically on navigation.
      localStorage.setItem(REFRESH_SITE_STORAGE_KEY, Date.now().toString()); // cross-tab reload via SessionKeepAlive

      await handlePostLoginRedirect();
    } else if (result?.generalError) {
      trackLoginError();
      setLoginError(result.generalError);

      // Keep generalError as a space so LoginForm marks the fields invalid (red borders)
      // without rendering visible error text — the snackbar above the form carries the message.
      return { emailError: result.emailError, passwordError: result.passwordError, generalError: ' ' };
    }

    return result;
  }

  async function handleRegister(prevState: RegisterState, formData: FormData): Promise<RegisterState> {
    const result = await registerAction(prevState, formData);

    if (result === null) {
      trackRegisterSuccess();

      // Do NOT call router.refresh() here — same reason as handleLogin:
      // middleware redirect on UNAUTHENTICATED_ONLY route races with router.push(addressHref).
      localStorage.setItem(REFRESH_SITE_STORAGE_KEY, Date.now().toString()); // cross-tab reload via SessionKeepAlive

      await handlePostLoginRedirect();
    } else if (result?.generalError || result?.emailError) {
      trackRegisterError();
    }

    return result;
  }

  async function handleForgotPassword(
    prevState: ForgotPasswordState,
    formData: FormData
  ): Promise<ForgotPasswordState> {
    const result = await forgotPasswordAction(prevState, formData);

    if (result === null) {
      trackForgotPasswordSuccess();
      setIsForgotPasswordSuccess(true);
    } else if (result?.generalError) {
      trackForgotPasswordError();
    }

    return result;
  }

  return { handleLogin, handleRegister, handleForgotPassword };
}
